import { defineTokens, defineSemanticTokens } from "@pandacss/dev";

// HSL-based palette
export const colors = defineTokens.colors({
	cream: {
		DEFAULT: { value: "hsl(12, 38%, 98%)" },
		50: { value: "hsl(12, 38%, 98%)" },
		100: { value: "hsl(12, 38%, 92%)" },
		200: { value: "hsl(12, 38%, 86%)" },
		300: { value: "hsl(12, 38%, 80%)" },
		400: { value: "hsl(12, 38%, 74%)" },
		500: { value: "hsl(12, 38%, 68%)" },
	},
	indigo: {
		DEFAULT: { value: "hsl(255, 60%, 2%)" },
		600: { value: "hsl(255, 57%, 28%)" },
		700: { value: "hsl(255, 58%, 20%)" },
		800: { value: "hsl(255, 61%, 12%)" },
		900: { value: "hsl(255, 60%, 8%)" },
		1000: { value: "hsl(255, 60%, 4%)" },
		1100: { value: "hsl(260, 60%, 2%)" },
	},
	apricot: {
		DEFAULT: { value: "hsl(354, 78%, 48%)" },
		100: { value: "hsl(354, 78%, 94%)" },
		200: { value: "hsl(354, 74%, 80%)" },
		300: { value: "hsl(354, 73%, 68%)" },
		400: { value: "hsl(354, 72%, 52%)" },
		500: { value: "hsl(353, 71%, 48%)" },
		600: { value: "hsl(353, 71%, 36%)" },
		700: { value: "hsl(353, 71%, 32%)" },
		800: { value: "hsl(353, 71%, 28%)" },
		900: { value: "hsl(353, 71%, 20%)" },
		1000: { value: "hsl(359, 70%, 16%)" },
	},
	lemon: {
		DEFAULT: { value: "hsl(45, 91%, 48%)" },
		100: { value: "hsl(45, 91%, 94%)" },
		200: { value: "hsl(45, 89%, 80%)" },
		300: { value: "hsl(50, 89%, 68%)" },
		400: { value: "hsl(50, 89%, 52%)" },
		500: { value: "hsl(50, 89%, 48%)" },
		600: { value: "hsl(50, 89%, 36%)" },
		700: { value: "hsl(50, 89%, 32%)" },
		800: { value: "hsl(50, 89%, 28%)" },
		900: { value: "hsl(50, 89%, 20%)" },
		1000: { value: "hsl(50, 89%, 16%)" },
	},
	clementine: {
		DEFAULT: { value: "hsl(15, 83%, 48%)" },
		100: { value: "hsl(15, 83%, 94%)" },
		200: { value: "hsl(15, 81%, 80%)" },
		300: { value: "hsl(15, 79%, 68%)" },
		400: { value: "hsl(15, 79%, 52%)" },
		500: { value: "hsl(17, 79%, 48%)" },
		600: { value: "hsl(17, 79%, 36%)" },
		700: { value: "hsl(17, 79%, 32%)" },
		800: { value: "hsl(17, 79%, 28%)" },
		900: { value: "hsl(17, 79%, 20%)" },
		1000: { value: "hsl(17, 79%, 16%)" },
	},
	lime: {
		DEFAULT: { value: "hsl(140, 33%, 48%)" },
		100: { value: "hsl(140, 33%, 94%)" },
		200: { value: "hsl(140, 33%, 80%)" },
		300: { value: "hsl(140, 33%, 68%)" },
		400: { value: "hsl(140, 33%, 52%)" },
		500: { value: "hsl(140, 33%, 48%)" },
		600: { value: "hsl(140, 33%, 36%)" },
		700: { value: "hsl(140, 33%, 32%)" },
		800: { value: "hsl(140, 33%, 28%)" },
		900: { value: "hsl(140, 33%, 20%)" },
		1000: { value: "hsl(140, 33%, 16%)" },
	},
	blueberry: {
		DEFAULT: { value: "hsl(229, 70%, 48%)" },
		100: { value: "hsl(229, 70%, 94%)" },
		200: { value: "hsl(229, 70%, 80%)" },
		300: { value: "hsl(229, 70%, 68%)" },
		400: { value: "hsl(229, 70%, 52%)" },
		500: { value: "hsl(229, 70%, 48%)" },
		600: { value: "hsl(229, 70%, 36%)" },
		700: { value: "hsl(229, 70%, 32%)" },
		800: { value: "hsl(229, 70%, 28%)" },
		900: { value: "hsl(229, 70%, 20%)" },
		1000: { value: "hsl(229, 70%, 16%)" },
	},
});

export const semanticColors = defineSemanticTokens.colors({
	shadow: {
		value: {
			// Shadow tint tied to indigo palette for theme-aware elevation.
			base: "{colors.indigo.1100}",
			_dark: "{colors.indigo.600}",
			_osDark: "{colors.indigo.600}",
		},
	},
	background: {
		DEFAULT: {
			value: {
				base: "{colors.cream.50}",
				_dark: "{colors.indigo.1100}",
				_osDark: "{colors.indigo.1100}",
			},
		},
		ghost: {
			value: {
				base: "{colors.cream.50}",
				_dark: "{colors.indigo.1100}",
				_osDark: "{colors.indigo.1100}",
			},
		},
		subtle: {
			value: {
				base: "{colors.cream.100}",
				_dark: "{colors.indigo.900}",
				_osDark: "{colors.indigo.900}",
			},
		},
		bold: {
			value: {
				base: "{colors.cream.200}",
				_dark: "{colors.indigo.700}",
				_osDark: "{colors.indigo.700}",
			},
		},
		loud: {
			value: {
				base: "{colors.apricot.300}",
				_dark: "{colors.apricot.1000}",
				_osDark: "{colors.apricot.1000}",
			},
		},
	},
	foreground: {
		DEFAULT: {
			value: {
				base: "{colors.indigo.900}",
				_dark: "{colors.cream.100}",
				_osDark: "{colors.cream.100}",
			},
		},
		ghost: {
			value: "{colors.foreground}",
		},
	},
	border: {
		value: {
			base: "{colors.indigo.800}",
			_dark: "{colors.indigo.600}",
			_osDark: "{colors.indigo.600}",
		},
	},
	accent: {
		DEFAULT: {
			value: {
				base: "{colors.apricot.300}",
				_dark: "{colors.apricot.200}",
				_osDark: "{colors.apricot.200}",
			},
		},
		text: {
			value: {
				base: "{colors.indigo.800}",
				_dark: "{colors.indigo.1000}",
				_osDark: "{colors.indigo.1000}",
			},
		},
	},
	success: {
		value: { base: "{colors.lime.200}", _dark: "{colors.lime.800}", _osDark: "{colors.lime.800}" },
	},
	fatal: {
		value: {
			base: "{colors.apricot.400}",
			_dark: "{colors.apricot.900}",
			_osDark: "{colors.apricot.900}",
		},
	},
	danger: {
		value: {
			base: "{colors.apricot.200}",
			_dark: "{colors.apricot.800}",
			_osDark: "{colors.apricot.800}",
		},
	},
	warning: {
		value: {
			base: "{colors.lemon.400}",
			_dark: "{colors.lemon.800}",
			_osDark: "{colors.lemon.800}",
		},
	},
	info: {
		value: {
			base: "{colors.blueberry.200}",
			_dark: "{colors.blueberry.300}",
			_osDark: "{colors.blueberry.300}",
		},
	},
	navigationButton: {
		DEFAULT: {
			value: "token(colors.background.bold/0)",
		},
		hover: {
			value: "token(colors.background.bold/50)",
		},
		active: {
			value: "token(colors.background.bold/100)",
		},
	},
	status: {
		DEFAULT: { value: "{colors.indigo.600}" },
		unavailable: { value: "{colors.foreground/20}" },
		latest: { value: "{colors.success}" },
		stale: { value: "{colors.lemon.200}" },
		inprogress: { value: "{colors.info}" },
	},
});

export type ColorTokens = typeof colors;
export type SemanticColorTokens = typeof semanticColors;
